/**
 * Brand Health module — mock widget loaders (prototype
 * `views/cards/BrandHealthCards.tsx`, `NpsCard.tsx`, `TopicSentimentSplitCard.tsx`).
 * Every card reads `scoped(query).own`: Brand Health is own-brand only.
 */
import type { WidgetLoaders } from '@/data-sources/contract';
import type { CategorySeries } from '@/data-sources/contract/widgets/brand-health';

import { aggregate, X_KEY } from '../engine/aggregate';
import { dimensionLabels } from '../engine/dimensions';
import { ORIGIN_VALUES } from '../engine/generator';
import { metrics } from '../engine/metrics';
import { pivot, pivotMetrics, type PivotResult } from '../engine/pivot';
import { scoped } from '../query';
import { comparisonOf, timeSeriesOf, voiceRows } from './shared';

/** Most valuable first; they sum to `engagement` (prototype `ENGAGEMENT_METRICS`). */
const ENGAGEMENT_METRICS = ['shares', 'comments', 'reactions'] as const;

/** Topic label → its option value, to recover the keys of a pivot's (labelled) categories. */
const TOPIC_BY_LABEL = new Map(
  Object.entries(dimensionLabels.topic).map(([value, label]) => [label, value]),
);

/** A pivot's series as contract series, one value per category row. */
function seriesOf(
  result: PivotResult,
  dimension: 'sentiment' | 'channel',
): CategorySeries['series'] {
  return result.series.map((s) => ({
    key: s.key,
    dimension,
    values: result.rows.map((row) => Number(row[s.key] ?? 0)),
  }));
}

export const brandHealthWidgetLoaders = {
  nps: async ({ query }) => {
    const s = scoped(query);
    const daily = aggregate(s.own, { metric: 'npsNet', from: s.from, to: s.to });
    const before = comparisonOf(s.ownPrevious, s.previousBounds, daily, { metric: 'npsNet' });
    return {
      daily: timeSeriesOf(daily, s.from, s.to, undefined, before),
      positiveShare: {
        current: metrics.positiveShare.reduce(s.own),
        previous: s.ownPrevious ? metrics.positiveShare.reduce(s.ownPrevious) : null,
      },
      mentions: s.own.length,
    };
  },

  'seeded-vs-organic': async ({ query }) => {
    const origin = pivot(scoped(query).own, { metric: 'mentions', xBy: 'origin' });
    return {
      origins: origin.rows.map((row) => {
        const label = String(row[X_KEY]);
        const key = label.toLowerCase();
        return {
          key,
          label,
          index: (ORIGIN_VALUES as readonly string[]).indexOf(key),
          mentions: Number(row.mentions ?? 0),
        };
      }),
    };
  },

  'sentiment-over-time': async ({ query }) => {
    const s = scoped(query);
    const spec = { metric: 'mentions', splitBy: 'sentiment' } as const;
    const sentiment = aggregate(s.own, { ...spec, from: s.from, to: s.to });
    const before = comparisonOf(s.ownPrevious, s.previousBounds, sentiment, spec);
    return {
      sentiment: timeSeriesOf(sentiment, s.from, s.to, 'sentiment', before),
      meanSentiment: {
        current: metrics.meanSentiment.reduce(s.own),
        previous: s.ownPrevious ? metrics.meanSentiment.reduce(s.ownPrevious) : null,
      },
      negativeShare: {
        current: metrics.negativeShare.reduce(s.own),
        previous: s.ownPrevious ? metrics.negativeShare.reduce(s.ownPrevious) : null,
      },
    };
  },

  'engagement-volume': async ({ query }) => {
    const s = scoped(query);
    const engagement = aggregate(s.own, { metric: 'engagement', from: s.from, to: s.to });
    const before = comparisonOf(s.ownPrevious, s.previousBounds, engagement, {
      metric: 'engagement',
    });
    return {
      engagement: timeSeriesOf(engagement, s.from, s.to, undefined, before),
      total: {
        current: metrics.engagement.reduce(s.own),
        previous: s.ownPrevious ? metrics.engagement.reduce(s.ownPrevious) : null,
      },
      rate: {
        current: metrics.engagementRate.reduce(s.own),
        previous: s.ownPrevious ? metrics.engagementRate.reduce(s.ownPrevious) : null,
      },
      mentions: s.own.length,
    };
  },

  'engagement-by-type': async ({ query }) => {
    const byType = pivotMetrics(scoped(query).own, {
      metrics: [...ENGAGEMENT_METRICS],
      splitBy: 'channel',
    });
    return {
      byType: {
        categories: ENGAGEMENT_METRICS.map((key) => ({ key })),
        series: seriesOf(byType, 'channel'),
      },
    };
  },

  'engagement-growth': async ({ query }) => {
    const s = scoped(query);
    const spec = { metric: 'engagement', splitBy: 'channel' } as const;
    const engagement = aggregate(s.own, { ...spec, from: s.from, to: s.to });
    const before = comparisonOf(s.ownPrevious, s.previousBounds, engagement, spec);
    return { engagement: timeSeriesOf(engagement, s.from, s.to, 'channel', before) };
  },

  'topic-sentiment-split': async ({ query, params }) => {
    const topics = pivot(voiceRows(scoped(query).own, params?.origin), {
      metric: 'mentions',
      xBy: 'topic',
      splitBy: 'sentiment',
      sort: 'value',
      sortBy: 'negative',
    });
    return {
      topics: {
        categories: topics.rows.map((row) => {
          const label = String(row[X_KEY]);
          return { key: TOPIC_BY_LABEL.get(label) ?? label, dimension: 'topic' as const };
        }),
        series: seriesOf(topics, 'sentiment'),
      },
    };
  },
} satisfies Partial<WidgetLoaders>;
